import { Container, Row, Col, Badge, Dropdown } from 'react-bootstrap';
import { useLocation, Navigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import { RiFileCopyFill, RiUpload2Fill } from '@remixicon/react';
import Copied from '../../../components/Copied/Copied';
import { useTranslation } from "react-i18next";
import InfoBanner from '../../../components/InfoBanner/InfoBanner.jsx';
import useSubscriptionStore from '../../../stores/subscriptionStore.js';
import { handleCopyToClipboard, downloadCSV, generateCSVExport } from '../../../utils/helpers.js'

function ProductGeneratedScreen() {
    const location = useLocation();
    const product = location.state?.product;
    const keyword = location.state?.keyword;
    const [showCopiedModal, setShowCopiedModal] = useState(false);
    const timerRef = useRef(null);
    const { t } = useTranslation();;
    const { isDemo } = useSubscriptionStore();

    if (!product || !keyword) {
        return (
            <Navigate to="/" />
        );
    }

    const handleExportShopify = () => {
        const data = generateCSVExport(product, 'shopify');
        downloadCSV(data, 'shopify-product.csv');
    };

    const handleExportWooCommerce = () => {
        const data = generateCSVExport(product, 'woocommerce');
        downloadCSV(data, 'woocommerce-product.csv');
    };

    return (
        <>
            <Container className="py-5">
                {isDemo && (
                    <InfoBanner />
                )}

                <div className="mb-4 d-flex justify-content-end">
                    <Dropdown>
                        <Dropdown.Toggle variant="primary" id="dropdown-basic">
                            {t('product.export')}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item onClick={handleExportShopify} className="d-flex align-items-center">
                                <RiUpload2Fill size={18} className="me-2" />
                                <span>{t('product.exportShopify')}</span>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleExportWooCommerce} className="d-flex align-items-center">
                                <RiUpload2Fill size={18} className="me-2" />
                                <span>{t('product.exportWooCommerce')}</span>
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                {/* Header with Original Keyword */}
                <Row className="mb-4">
                    <Col xs={12} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('app.keyword')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(keyword, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('product.copy')}
                                />
                            </div>
                            <p className="card-text">{keyword}</p>
                        </div>
                    </Col>
                </Row>

                {/* Slug Section */}
                <Row className="mb-4">
                    <Col xs={12} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('product.slug')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(product.slug, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('product.copy')}
                                />
                            </div>
                            <p className="card-text">{product.slug}</p>
                        </div>
                    </Col>
                </Row>

                {/* Three Information Cards */}
                <Row className="mb-4 g-4">
                    <Col xs={12} md={6} lg={4} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('product.title')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(product.title, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('product.copy')}
                                />
                            </div>
                            <p className="card-text">{product.title}</p>
                        </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('product.shortDesc')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(product.shortDescription, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('product.copy')}
                                />
                            </div>
                            <p className="card-text">{product.shortDescription}</p>
                        </div>
                    </Col>
                    <Col xs={12} md={12} lg={4} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('product.metaDesc')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(product.metaDescription, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('product.copy')}
                                />
                            </div>
                            <p className="card-text">{product.metaDescription}</p>
                        </div>
                    </Col>
                </Row>

                {/* Detailed Description Section */}
                <Row className="mb-4">
                    <Col xs={12} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h3 className="card-title text-uppercase">{t('app.content')}</h3>
                                    <Badge bg="light" text="dark">{t('app.html')}</Badge>
                                </div>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(product.descriptionHtml, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('product.copy')}
                                />
                            </div>
                            <div className="max-h-600 overflow-y-auto hide-scrollbar" dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
                        </div>
                    </Col>
                </Row>

                {/* Relevant Tags Section */}
                <Row>
                    <Col xs={12} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('product.tags')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(product.tags.join(', '), timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title="Copier tous les mots-clés"
                                />
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {product.tags.map((kw, index) => (
                                    <Badge key={index} bg="light" text="dark" className="p-2">
                                        {kw}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Copied
                show={showCopiedModal}
                onHide={() => setShowCopiedModal(false)}
            />
        </>
    );
}

export default ProductGeneratedScreen;